// Native desktop hooks (F4 contract v1), active only when the genuine preload bridge exists.
// Dial links only prefill the dialer; nothing here places, answers or ends a call. Upload and
// local-capture hooks are intentionally not wired: those features are not available yet.
import type { Call } from '@bcio-voice/contracts';
import { useEffect, useRef } from 'react';
import { useWorkspace } from '../state/workspace.tsx';
import { useRouter } from '../lib/router.tsx';
import { useAnnouncer } from '../ui/Announcer.tsx';
import { isIncomingFor } from '../call/callStore.ts';
import { partyName } from '../features/phone/callView.ts';
import { type DesktopTrayState, useDesktop } from './bridge.ts';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ignore = () => undefined;

/** Tray state from real call and phone state only. */
export function trayStateFor(input: { ringing: boolean; activeCalls: number; muted: boolean; phoneReady: boolean }): DesktopTrayState {
  if (input.ringing) return 'ringing';
  if (input.activeCalls > 0) return input.muted ? 'muted' : 'on_call';
  return input.phoneReady ? 'idle' : 'offline';
}

export function DesktopHooks() {
  const desktop = useDesktop();
  const ws = useWorkspace();
  const { navigate } = useRouter();
  const { announce } = useAnnouncer();
  const latest = useRef({ ws, navigate, announce });
  latest.current = { ws, navigate, announce };

  // Subscriptions: attach once per bridge; read the latest workspace through the ref.
  useEffect(() => {
    if (!desktop) return;
    const offs = [
      desktop.onDialLink(link => {
        const input = link.e164 ?? link.number;
        latest.current.ws.requestDial({ kind: 'pstn', input, fromNumberId: null }, null, 'link');
        latest.current.navigate('/phone');
        latest.current.announce('A phone number from a link is in the dialer. Check it, then press Call.');
      }),
      desktop.onPower(event => {
        // Resume or unlock: re-register the phone and catch up on missed events now.
        if (event.kind === 'resume' || event.kind === 'unlock-screen') {
          latest.current.ws.phone.retry();
          latest.current.ws.reconnectStream();
        }
      }),
      desktop.onTrayCommand(event => {
        if (event.command !== 'mute_toggle') return;
        const w = latest.current.ws;
        const call = w.liveCalls.find(c => !isIncomingFor(c, w.userId));
        const ec = call ? w.callControl.engineCall(call) : null;
        if (call && ec && ec.microphone !== 'none') w.callControl.setMuted(call, !ec.muted);
      }),
      desktop.onNotificationClicked(event => {
        if ((event.kind === 'incoming_call' || event.kind === 'missed_call') && UUID.test(event.ref)) latest.current.navigate(`/inbox/calls/${encodeURIComponent(event.ref)}`);
      }),
      desktop.devices.onPermissionDenied(event => {
        if (event.permission === 'media' || event.permission === 'microphone') latest.current.announce('Microphone access was blocked. Allow it in your system settings, then try again.');
      }),
    ];
    return () => { for (const off of offs) off(); };
  }, [desktop]);

  // Ringing, tray and call activity follow real state.
  const incoming = ws.liveCalls.find(c => isIncomingFor(c, ws.userId)) ?? null;
  const activeCalls = ws.liveCalls.length;
  const muted = ws.liveCalls.some(c => ws.callControl.engineCall(c)?.muted === true);
  const tray = trayStateFor({ ringing: !!incoming, activeCalls, muted, phoneReady: ws.readiness.state === 'ready' });
  const ringingId = incoming?.id ?? null;

  useEffect(() => { void desktop?.setRinging(ringingId ? { call_id: ringingId } : null).catch(ignore); }, [desktop, ringingId]);
  useEffect(() => { void desktop?.setTrayState(tray).catch(ignore); }, [desktop, tray]);
  useEffect(() => { void desktop?.setCallActivity({ active_calls: activeCalls }).catch(ignore); }, [desktop, activeCalls]);
  useEffect(() => () => {
    // Leaving the workspace (sign-out, switch): nothing is ringing or active from this renderer.
    void desktop?.setRinging(null).catch(ignore);
    void desktop?.setCallActivity({ active_calls: 0 }).catch(ignore);
    void desktop?.setTrayState('offline').catch(ignore);
  }, [desktop]);

  // Notifications: incoming (main shows them only when unfocused, with caller detail only if the
  // user chose it) and missed. Never transcript or note text.
  const seen = useRef(new Map<string, Call>());
  useEffect(() => {
    if (!desktop) return;
    const now = new Map<string, Call>();
    for (const call of ws.liveCalls) {
      if (!isIncomingFor(call, ws.userId)) continue;
      now.set(call.id, call);
      if (!seen.current.has(call.id)) {
        const name = partyName(call, ws.userId, ws.userName);
        void desktop.notify({ kind: 'incoming_call', ref: call.id, caller: { display_name: name, e164: call.direction === 'inbound' ? call.from.e164 : null } }).catch(ignore);
      }
    }
    for (const [id] of seen.current) {
      if (now.has(id)) continue;
      const ended = ws.calls.get(id);
      if (ended?.outcome === 'missed') void desktop.notify({ kind: 'missed_call', ref: id }).catch(ignore);
    }
    seen.current = now;
  }, [desktop, ws.liveCalls, ws.calls, ws.userId, ws.userName]);

  return null;
}
